import { RIDE_AREA_TITLES_DELIMITER } from "../../constants";
import type { IRideTableDto } from "../../dto";
import { RidesKanbanSortEnum } from "../../enums";
import type { IDropdownItem } from "../../models";
import { unique } from "../array.helpers";
import { dateToComparableNumber } from "../date.helpers";

const areaLabelsCollator = new Intl.Collator(undefined, {
  sensitivity: "base",
  numeric: true,
});

const compareIdsAscending = (a: IRideTableDto, b: IRideTableDto): number =>
  a.id - b.id;

const compareIdsDescending = (a: IRideTableDto, b: IRideTableDto): number =>
  b.id - a.id;

/** Latest first. A missing date compares as 0, so those rides go last; equal dates fall back to the newest id. */
const compareDatesDescending =
  (field: "createdAt" | "updatedAt") =>
  (a: IRideTableDto, b: IRideTableDto): number =>
    dateToComparableNumber(b[field]) - dateToComparableNumber(a[field]) ||
    compareIdsDescending(a, b);

const getAreaLabelsByRideId = ({
  rides,
  areaDropdownItems,
}: {
  rides: IRideTableDto[];
  areaDropdownItems: IDropdownItem[];
}): Map<number, string> => {
  const areaLabelsById = new Map(
    areaDropdownItems.map(item => [String(item.value), item.label]),
  );

  return new Map(
    rides.map(ride => {
      const areaLabels = (ride.metadata?.areaIds ?? [])
        .map(areaId => areaLabelsById.get(String(areaId)))
        .filter((label): label is string => !!label);

      // Same text as the card's area line, so the order follows what the user reads.
      return [ride.id, unique(areaLabels).join(RIDE_AREA_TITLES_DELIMITER)];
    }),
  );
};

/** A-Z; rides without a resolved area go last. */
const compareAreaLabels = (aLabel: string, bLabel: string): number => {
  if (aLabel === bLabel) {
    return 0;
  }

  if (!aLabel) {
    return 1;
  }

  if (!bLabel) {
    return -1;
  }

  return areaLabelsCollator.compare(aLabel, bLabel);
};

/**
 * Orders one Kanban column. Returns a new array; `rides` is not mutated.
 *
 * Ties fall back to the ride id, which loosely follows creation order: newest first for the two Z-A options,
 * lowest first for Area A-Z.
 */
export const sortKanbanRides = ({
  rides,
  sort,
  areaDropdownItems,
}: {
  rides: IRideTableDto[];
  sort: RidesKanbanSortEnum;
  areaDropdownItems: IDropdownItem[];
}): IRideTableDto[] => {
  switch (sort) {
    case RidesKanbanSortEnum.UpdatedDesc:
      return rides.toSorted(compareDatesDescending("updatedAt"));
    case RidesKanbanSortEnum.CreatedDesc:
      return rides.toSorted(compareDatesDescending("createdAt"));
    case RidesKanbanSortEnum.AreaAsc: {
      const areaLabelsByRideId = getAreaLabelsByRideId({
        rides,
        areaDropdownItems,
      });

      return rides.toSorted(
        (a, b) =>
          compareAreaLabels(
            areaLabelsByRideId.get(a.id) ?? "",
            areaLabelsByRideId.get(b.id) ?? "",
          ) || compareIdsAscending(a, b),
      );
    }
  }
};
